import { z } from 'zod';
import { dateOnlySchema, paginationSchema } from './common.js';
import { holderInputSchema } from './asset.js';

/**
 * Mitarbeiter sind Geräteträger, keine Systemkonten — das ist die
 * zentrale Vereinfachung gegenüber Snipe-IT (siehe Konzept Abschnitt 2).
 */
export const employeeSchema = z.object({
  id: z.number().int(),
  first_name: z.string(),
  last_name: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  employee_no: z.string().nullable(),
  location: z.object({ id: z.number().int(), name: z.string() }).nullable(),
  is_active: z.boolean(),
  exit_date: z.string().nullable(),
  notes: z.string().nullable(),
  assets_count: z.number().int(),
  /** Gesetzt, wenn die Person zusätzlich ein Systemkonto hat. */
  user_id: z.number().int().nullable(),
  snipe_id: z.number().int().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Employee = z.infer<typeof employeeSchema>;

export const employeeCreateSchema = z.object({
  first_name: z.string().trim().min(1).max(80),
  last_name: z.string().trim().min(1).max(80),
  email: z.string().trim().email().max(200).nullable().optional(),
  employee_no: z.string().trim().max(60).nullable().optional(),
  location_id: z.number().int().positive().nullable().optional(),
  notes: z.string().trim().max(4000).nullable().optional(),
});

export const employeeUpdateSchema = employeeCreateSchema.partial().extend({
  is_active: z.boolean().optional(),
  exit_date: dateOnlySchema.nullable().optional(),
});

/** Austritt oder Nachfolge: alle Geräte einer Person in einem Vorgang bewegen. */
export const employeeMoveAllSchema = z.object({
  to: holderInputSchema,
  /** Leer = alle Geräte der Person. */
  asset_ids: z.array(z.number().int().positive()).optional(),
  note: z.string().trim().max(1000).optional(),
  /** Person zugleich als ausgeschieden markieren. */
  deactivate: z.boolean().default(false),
  exit_date: dateOnlySchema.optional(),
  request_confirmation: z.boolean().default(false),
  dry_run: z.boolean().default(false),
});

export const employeeListQuerySchema = paginationSchema.extend({
  q: z.string().trim().max(200).optional(),
  location_id: z.coerce.number().int().positive().optional(),
  is_active: z.coerce.boolean().optional(),
  with_assets: z.coerce.boolean().optional(),
  sort: z.enum(['name', 'assets_count', 'created_at']).default('name'),
  order: z.enum(['asc', 'desc']).default('asc'),
});
